import styled from 'styled-components'
import { screenSizes } from '@shipt/design-system-themes'
import { Body, Headline } from '@shipt/design-system-typography'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { type HeadlineImageCardData } from '@/features/cms/components/CMS/types'
import { CMSErrorHandler } from '@/features/cms/components/CMS/CMSErrorHandler'
import { getCMSWrapperProps } from '@/features/cms/components/CMS/utils/getCMSWrapperProps'

/**
 * This component corresponds to the `headline_image_card` content type in the CMS.
 *
 * - **Staging** - https://cms-staging.shipt.com/content-type/pool/headline_image_card
 * - **Production** - https://cms.shipt.com/content-type/pool/headline_image_card
 */
export const HeadlineImageCard = ({
  content_type_id,
  id,
  data: { heading, subheading, image },
}: HeadlineImageCardData) => {
  try {
    return (
      <Card {...getCMSWrapperProps({ content_type_id, id })}>
        <HIWImage
          src={image.src}
          alt={image.alt}
          width={image.width}
          height={image.height}
          style={{ height: 'auto' }}
        />
        <Caption>
          <CaptionHeading as="h3" size="lg">
            {heading}
          </CaptionHeading>
          {!!subheading && (
            <CaptionContent size="md" variant="secondary">
              {subheading}
            </CaptionContent>
          )}
        </Caption>
      </Card>
    )
  } catch (error) {
    return <CMSErrorHandler error={error} contentTypeId={content_type_id} id={id} />
  }
}

const Card = styled.div`
  max-width: 21.5rem;
  text-align: center;
  margin: 0 auto;

  @media ${screenSizes.tablet} {
    flex: 3;
    margin: 0 0.75rem;
    max-width: 100%;
  }
`

const HIWImage = styled(Image)`
  border-radius: 0.5rem;
  min-width: 4rem;
  min-height: 4rem;
`

const Caption = styled.div`
  max-width: 18.75rem;
  margin: 1rem auto 2rem;

  h3 {
    font-size: 1.125rem;
    letter-spacing: 0.0156rem;
    line-height: 1.22;
    margin-bottom: 0.5rem;
  }

  p {
    margin-bottom: 1.5rem;
  }
`

const CaptionContent = styled(Body)`
  font-size: 1rem;
  font-weight: 300;
  font-style: normal;
  line-height: 1.63;
  letter-spacing: normal;
`

const CaptionHeading = styled(Headline)`
  margin: 0 auto 0.5rem;
  max-width: 20rem;
`
